/**
 * Перенос `utils/storage_helper.dart`.
 *
 * Вместо `shared_preferences` — `localStorage`: те же ключи, те же правила,
 * та же форма синглтона. Отличий два, и оба вынужденные:
 *
 * 1. `localStorage` синхронный, поэтому [StorageHelper.initialize] по сути
 *    ничего не ждёт. Асинхронная подпись и флаги сохранены: `main.tsx`
 *    дожидается её ровно так же, как это делал `main.dart`.
 * 2. Хеш пин-кода считает `crypto.subtle` — он асинхронный, так что
 *    [StorageHelper.savePinHash] и [StorageHelper.matchesSavedPin] стали
 *    возвращать `Promise` (экран PIN их дожидается).
 */

import { appLanguageFromString, type AppLanguage } from '@/domain/models/appLanguage';
import {
  configModelFromJson,
  configModelToJson,
  type ConfigModel,
} from '@/domain/models/configModel';
import { DiagnosticsLog } from '@/utils/diagnostics/diagnosticsLog';

/**
 * Перенос `enum StorageKey`. Значение члена — это и есть его `param`
 * (в Dart они всюду совпадали), а `param` — имя ключа в хранилище.
 */
export const StorageKey = {
  language: 'language',
  baseUrl: 'baseUrl',
  person: 'person',
  config: 'config',
  pinHash: 'pinHash',
  askBaseUrlAfterExit: 'askBaseUrlAfterExit',
  tabletPassword: 'tabletPassword',
  tabletToken: 'tabletToken',
  tabletRefreshToken: 'tabletRefreshToken',
  tabletCredentialsUrl: 'tabletCredentialsUrl',
  knownServers: 'knownServers',
} as const;

export type StorageKey = (typeof StorageKey)[keyof typeof StorageKey];

/** Аналог `StorageKey.values`: порядок объявления сохранён. */
export const STORAGE_KEY_VALUES: readonly StorageKey[] = Object.values(StorageKey);

/** Пароль и токены планшета вместе с адресом, которому они принадлежат. */
export interface TabletCredentialsSnapshot {
  readonly url: string | null;
  readonly password: string | null;
  readonly token: string | null;
  readonly refreshToken: string | null;
}

/**
 * Соль для хеша пин-кода.
 *
 * Шестизначный код перебирается за секунды даже с солью — она защищает
 * от случайного прочтения значения из shared_prefs, не от подготовленного
 * человека. Для другого уровня нужен flutter_secure_storage.
 */
const PIN_SALT = 'ai_live_porters.pin.v1';

/** Ключ пробной записи: по ней проверяется, что хранилище вообще пишет. */
const PROBE_KEY = '__storageHelper.probe__';

const PROBE_VALUE = '1';

/** Как в хранилище выглядит `true` и `false` (`setBool` в Dart). */
const BOOL_TRUE = 'true';
const BOOL_FALSE = 'false';

/** Целое число десятичной записью — ничего другого `getInt` не примет. */
const INTEGER_PATTERN = /^-?\d+$/;

/** `const []` из Dart: одна и та же пустая ссылка, менять её нельзя. */
const EMPTY_SERVERS: readonly string[] = Object.freeze([]);

/** Хвостовые слеши, которые не считаются частью адреса. */
const TRAILING_SLASHES_PATTERN = /\/+$/;

/**
 * Адрес без хвостовых слешей и пробелов по краям.
 *
 * В Dart это правило было записано дважды — во `sameServer` и в
 * `addKnownServer`; здесь оно одно на обоих.
 */
function normalizeServerUrl(url: string): string {
  return url.trim().replace(TRAILING_SLASHES_PATTERN, '');
}

/**
 * Перенос `_StorageHandler`.
 *
 * Объявлен до [StorageHelper] намеренно: статическое поле `instance`
 * создаётся при загрузке модуля и не увидело бы класс, объявленный ниже.
 *
 * `SharedPreferences` хранил значения по типам, `localStorage` — только
 * строки. Поэтому число пишется десятичной записью, флаг — `'true'`/
 * `'false'`, список строк — JSON-массивом; значение, которое не читается
 * обратно, считается отсутствующим (`null`), как отсутствующий ключ в Dart.
 *
 * Любое обращение к `localStorage` может бросить: приватный режим Safari,
 * запрет cookie, переполненная квота. Первый же сбой переводит обработчик
 * на карту в памяти и пишется в журнал — киоск продолжает работать, просто
 * забывает всё после перезагрузки страницы. Уронить запуск хранилище
 * не может.
 */
class StorageHandler {
  #storage: Storage | null = null;

  /** Пробовали ли уже открыть хранилище: проверка делается один раз. */
  #probed = false;

  /**
   * Зеркало всего, что записано в этом сеансе. Оно же — запасное
   * хранилище, когда `localStorage` недоступен. Неизменяемое: каждая
   * запись создаёт новый объект, старый не меняется.
   */
  #memory: Readonly<Record<string, string | undefined>> = {};

  async initialize(): Promise<void> {
    // `localStorage` открывать не нужно, но проверить — стоит: пусть
    // недоступное хранилище всплывёт на запуске, а не на первой записи.
    this.#open();
  }

  getString(key: StorageKey): string | null {
    return this.#read(key);
  }

  getInt(key: StorageKey): number | null {
    const raw = this.#read(key);
    if (raw === null) return null;
    if (!INTEGER_PATTERN.test(raw)) return null;
    return Number(raw);
  }

  setInt(key: StorageKey, value: number): boolean {
    return this.#write(key, value.toString());
  }

  setString(key: StorageKey, value: string): boolean {
    return this.#write(key, value);
  }

  getBool(key: StorageKey): boolean | null {
    const raw = this.#read(key);
    if (raw === BOOL_TRUE) return true;
    if (raw === BOOL_FALSE) return false;
    return null;
  }

  setBool(key: StorageKey, value: boolean): boolean {
    return this.#write(key, value ? BOOL_TRUE : BOOL_FALSE);
  }

  getStringList(key: StorageKey): readonly string[] | null {
    const raw = this.#read(key);
    if (raw === null) return null;
    try {
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return null;
      const strings = parsed.filter((item: unknown): item is string => typeof item === 'string');
      // Список с посторонним значением читать нечестно — считаем его битым.
      return strings.length === parsed.length ? strings : null;
    } catch {
      // Битый JSON — ключа как будто нет.
      return null;
    }
  }

  setStringList(key: StorageKey, value: readonly string[]): boolean {
    return this.#write(key, JSON.stringify(value));
  }

  delete(key: StorageKey): boolean {
    this.#memory = Object.fromEntries(
      Object.entries(this.#memory).filter(([name]) => name !== key),
    );
    const storage = this.#open();
    if (storage === null) return true;
    try {
      storage.removeItem(key);
    } catch (e) {
      this.#degrade(`удаление ${key}`, e);
    }
    return true;
  }

  #read(key: StorageKey): string | null {
    const storage = this.#open();
    if (storage === null) return this.#memory[key] ?? null;
    try {
      return storage.getItem(key);
    } catch (e) {
      this.#degrade(`чтение ${key}`, e);
      return this.#memory[key] ?? null;
    }
  }

  /**
   * Запись всегда идёт и в память: если `localStorage` отвалится посреди
   * сеанса, записанное до этого никуда не денется.
   *
   * Возвращает `true` так же, как `SharedPreferences.set*`: память
   * принимает значение в любом случае, а сбой самого хранилища — повод
   * для журнала, а не для отказа экрану.
   */
  #write(key: StorageKey, value: string): boolean {
    this.#memory = { ...this.#memory, [key]: value };
    const storage = this.#open();
    if (storage === null) return true;
    try {
      storage.setItem(key, value);
    } catch (e) {
      this.#degrade(`запись ${key}`, e);
    }
    return true;
  }

  /** Хранилище или `null`, если оно недоступно. Проверяется один раз. */
  #open(): Storage | null {
    if (this.#probed) return this.#storage;
    this.#probed = true;
    try {
      // Сам доступ к `window.localStorage` бросает, когда хранилище
      // запрещено настройками браузера, — поэтому он внутри try.
      const storage = window.localStorage;
      // Пробная запись: в приватном режиме Safari хранилище есть,
      // но `setItem` бросает QuotaExceededError.
      storage.setItem(PROBE_KEY, PROBE_VALUE);
      storage.removeItem(PROBE_KEY);
      this.#storage = storage;
    } catch (e) {
      this.#storage = null;
      this.#report(`недоступно, работаем в памяти: ${String(e)}`);
    }
    return this.#storage;
  }

  /**
   * Хранилище отказало посреди работы — дальше только память. Повторных
   * попыток нет намеренно: иначе каждое чтение писало бы в журнал.
   */
  #degrade(operation: string, error: unknown): void {
    this.#storage = null;
    this.#report(`сбой (${operation}), дальше только память: ${String(error)}`);
  }

  #report(message: string): void {
    console.warn(`[StorageHelper] ${message}`);
    DiagnosticsLog.instance.event(`хранилище: ${message}`, { isError: true });
  }
}

export class StorageHelper {
  private constructor() {}

  static instance: StorageHelper = new StorageHelper();

  /** Свежий экземпляр для тестов — в Dart его не было. */
  static testInstance(): StorageHelper {
    return new StorageHelper();
  }

  readonly #handler: StorageHandler = new StorageHandler();

  #isInitialized = false;

  #isInitializing = false;

  get isInitialized(): boolean {
    return this.#isInitialized;
  }

  get isInitializing(): boolean {
    return this.#isInitializing;
  }

  async initialize(): Promise<void> {
    if (this.#isInitializing || this.isInitialized) {
      return;
    }
    try {
      this.#isInitializing = true;
      await this.#handler.initialize();
      this.#isInitialized = true;
    } finally {
      this.#isInitializing = false;
    }
  }

  get baseUrl(): string | null {
    return this.#handler.getString(StorageKey.baseUrl);
  }

  get person(): number | null {
    return this.#handler.getInt(StorageKey.person);
  }

  async setPerson(person: number): Promise<boolean> {
    return this.#handler.setInt(StorageKey.person, person);
  }

  /** Хеш последнего кода, принятого сервером. */
  get pinHash(): string | null {
    return this.#handler.getString(StorageKey.pinHash);
  }

  /**
   * Запоминает код после успешной серверной проверки, чтобы из киоска
   * можно было выйти, когда сети нет.
   */
  async savePinHash(pin: number): Promise<boolean> {
    return this.#handler.setString(StorageKey.pinHash, await hashPin(pin));
  }

  /**
   * Совпадает ли код с сохранённым. false, если ничего не сохранено.
   *
   * Порядок проверок тот же, что в Dart: без сохранённого хеша код даже
   * не считается — а значит, отсутствие `crypto.subtle` тут не всплывёт.
   */
  async matchesSavedPin(pin: number): Promise<boolean> {
    const saved = this.pinHash;
    return saved !== null && saved === (await hashPin(pin));
  }

  /**
   * Сохраняет адрес сервера.
   *
   * Если адрес сменился, сохранённый PIN сбрасывается: код от прежнего
   * сервера не должен открывать офлайн-выход на новом. Хвостовой слеш
   * сменой адреса не считается. Выбранный аватар сбрасывается тоже: id
   * персон у разных планшетов пересекаются, и прежний id выбрал бы чужую.
   * Пароль и токены планшета сбрасываются, если принадлежат другому
   * адресу (другой slug — другой планшет):
   * сравнение с [tabletCredentialsUrl], а не с прежним baseUrl, чтобы
   * не стереть креды, сохранённые за мгновение до setBaseUrl.
   */
  async setBaseUrl(baseUrl: string): Promise<boolean> {
    const current = this.baseUrl;
    if (current !== null && !StorageHelper.sameServer(current, baseUrl)) {
      await this.delete(StorageKey.pinHash);
    }
    if (!this.isCurrentBaseUrl(baseUrl)) {
      await this.delete(StorageKey.person);
    }
    const credentialsUrl = this.tabletCredentialsUrl;
    if (credentialsUrl !== null && !StorageHelper.sameServer(credentialsUrl, baseUrl)) {
      await this.clearTabletCredentials();
    }
    return this.#handler.setString(StorageKey.baseUrl, baseUrl);
  }

  /** Совпадает ли адрес с сохранённым. false, если адреса ещё нет. */
  isCurrentBaseUrl(url: string): boolean {
    const current = this.baseUrl;
    return current !== null && StorageHelper.sameServer(current, url);
  }

  /** Один и тот же адрес: хвостовой слеш и пробелы по краям не в счёт. */
  static sameServer(a: string, b: string): boolean {
    return normalizeServerUrl(a) === normalizeServerUrl(b);
  }

  // -- Авторизация планшета (tablets/auth) ----------------------------------

  /**
   * Пароль планшета, принятый сервером. Хранится как pinHash — в
   * shared_prefs (TODO: flutter_secure_storage для обоих). Может
   * отсутствовать при живых токенах: их выдаёт и верный PIN.
   */
  get tabletPassword(): string | null {
    return this.#handler.getString(StorageKey.tabletPassword);
  }

  get tabletToken(): string | null {
    return this.#handler.getString(StorageKey.tabletToken);
  }

  get tabletRefreshToken(): string | null {
    return this.#handler.getString(StorageKey.tabletRefreshToken);
  }

  /** Адрес (`root/slug`), для которого сохранены пароль и токены. */
  get tabletCredentialsUrl(): string | null {
    return this.#handler.getString(StorageKey.tabletCredentialsUrl);
  }

  /** Совпадает ли адрес с тем, для которого сохранены креды. */
  credentialsMatch(baseUrl: string): boolean {
    const saved = this.tabletCredentialsUrl;
    return saved !== null && StorageHelper.sameServer(saved, baseUrl);
  }

  /**
   * Есть ли у планшета [baseUrl] сессия в этом браузере — токены, выданные
   * паролем или верным PIN. Есть — пароль при входе не спрашивается:
   * протухший access-токен обновит refresh в фоне.
   */
  hasTabletSession(baseUrl: string): boolean {
    return this.credentialsMatch(baseUrl) && this.tabletToken !== null;
  }

  async saveTabletCredentials({
    baseUrl,
    password,
    token,
    refreshToken,
  }: {
    baseUrl: string;
    password: string;
    token: string;
    refreshToken: string;
  }): Promise<void> {
    this.#handler.setString(StorageKey.tabletCredentialsUrl, baseUrl);
    this.#handler.setString(StorageKey.tabletPassword, password);
    await this.saveTabletTokens({ token, refreshToken });
  }

  /**
   * Токены без пароля — их выдал `check-pin`. Пароль того же адреса
   * остаётся (по нему можно перелогиниться, когда refresh протухнет),
   * пароль другого адреса к этим токенам не относится и стирается.
   */
  async saveTabletSession({
    baseUrl,
    token,
    refreshToken,
  }: {
    baseUrl: string;
    token: string;
    refreshToken: string;
  }): Promise<void> {
    if (!this.credentialsMatch(baseUrl)) await this.delete(StorageKey.tabletPassword);
    this.#handler.setString(StorageKey.tabletCredentialsUrl, baseUrl);
    await this.saveTabletTokens({ token, refreshToken });
  }

  /** refresh возвращает только новый token — refreshToken тогда не трогаем. */
  async saveTabletTokens({
    token,
    refreshToken,
  }: {
    token: string;
    refreshToken?: string | null;
  }): Promise<void> {
    this.#handler.setString(StorageKey.tabletToken, token);
    if (refreshToken !== undefined && refreshToken !== null) {
      this.#handler.setString(StorageKey.tabletRefreshToken, refreshToken);
    }
  }

  async clearTabletCredentials(): Promise<void> {
    await this.delete(StorageKey.tabletPassword);
    await this.delete(StorageKey.tabletToken);
    await this.delete(StorageKey.tabletRefreshToken);
    await this.delete(StorageKey.tabletCredentialsUrl);
  }

  /** Креды как есть — чтобы вернуть их, если новый адрес не подтвердился. */
  get tabletCredentialsSnapshot(): TabletCredentialsSnapshot {
    return {
      url: this.tabletCredentialsUrl,
      password: this.tabletPassword,
      token: this.tabletToken,
      refreshToken: this.tabletRefreshToken,
    };
  }

  /** Возвращает креды из [snapshot]; `null` в снимке — ключа не было. */
  async restoreTabletCredentials(snapshot: TabletCredentialsSnapshot): Promise<void> {
    const put = async (key: StorageKey, value: string | null): Promise<void> => {
      if (value === null) {
        await this.delete(key);
      } else {
        this.#handler.setString(key, value);
      }
    };

    await put(StorageKey.tabletCredentialsUrl, snapshot.url);
    await put(StorageKey.tabletPassword, snapshot.password);
    await put(StorageKey.tabletToken, snapshot.token);
    await put(StorageKey.tabletRefreshToken, snapshot.refreshToken);
  }

  // -- Запомненные серверы для экрана адреса ---------------------------------

  /**
   * Домены, прошедшие успешную проверку. Предустановленные здесь
   * не хранятся — они всегда в списке из [Endpoints.knownServerPresets].
   */
  get knownServers(): readonly string[] {
    return this.#handler.getStringList(StorageKey.knownServers) ?? EMPTY_SERVERS;
  }

  /**
   * Запоминает сервер после успешной проверки — до неё не вызывается,
   * чтобы опечатки не засоряли список. [root] — нормализованный
   * `scheme://host` без хвостового слеша.
   */
  async addKnownServer(root: string): Promise<void> {
    const normalized = normalizeServerUrl(root);
    if (normalized.length === 0) return;
    const servers = this.knownServers;
    if (servers.includes(normalized)) return;
    this.#handler.setStringList(StorageKey.knownServers, [...servers, normalized]);
  }

  async removeKnownServer(root: string): Promise<void> {
    const servers = this.knownServers.filter((s) => s !== root);
    this.#handler.setStringList(StorageKey.knownServers, servers);
  }

  /**
   * Спросить адрес сервера при следующем запуске.
   *
   * Ставится при выходе из приложения по PIN: выход уже доказал, что это
   * администратор. Отдельной кнопки на экран адреса нет намеренно — через
   * неё туда попал бы любой, кто знает код.
   */
  get askBaseUrlAfterExit(): boolean {
    return this.#handler.getBool(StorageKey.askBaseUrlAfterExit) ?? false;
  }

  async setAskBaseUrlAfterExit(value: boolean): Promise<boolean> {
    if (!value) return this.delete(StorageKey.askBaseUrlAfterExit);
    return this.#handler.setBool(StorageKey.askBaseUrlAfterExit, true);
  }

  /**
   * Язык интерфейса. В хранилище лежит `param` (`uz`, `en`, `ru`, `ar`) —
   * то же, что писал Dart через `AppLanguage.toString()`.
   */
  get language(): AppLanguage | null {
    try {
      const string = this.#handler.getString(StorageKey.language);
      if (string === null) {
        return null;
      } else {
        return appLanguageFromString(string);
      }
    } catch {
      // In case Language not exist
      return null;
    }
  }

  async saveLanguage(language: AppLanguage): Promise<void> {
    this.#handler.setString(StorageKey.language, language);
  }

  /**
   * Последний сохранённый конфиг.
   *
   * Разбор здесь не защищён намеренно — как и в Dart: битое значение
   * бросает (у zod это `ZodError`), и слой исключений превращает его
   * в `badContract`, вместо того чтобы молча показать пустой экран.
   */
  get config(): ConfigModel | null {
    const json = this.#handler.getString(StorageKey.config);
    if (json === null) {
      return null;
    }
    const parsed: unknown = JSON.parse(json);
    return configModelFromJson(parsed);
  }

  async saveConfig(config: ConfigModel): Promise<boolean> {
    try {
      const json = JSON.stringify(configModelToJson(config));
      return this.#handler.setString(StorageKey.config, json);
    } catch (e) {
      // Конфиг не сериализуется — сохранять нечего, но и падать незачем:
      // он всё равно придёт с сервера при следующем запуске.
      DiagnosticsLog.instance.event(`конфиг не сохранён: ${String(e)}`, { isError: true });
      return false;
    }
  }

  async delete(key: StorageKey): Promise<boolean> {
    return this.#handler.delete(key);
  }

  async removeAll(): Promise<void> {
    const keys = STORAGE_KEY_VALUES;
    for (const key of keys) {
      await this.delete(key);
    }
  }
}

/**
 * Перенос `_hashPin`.
 *
 * В браузере sha256 считает `crypto.subtle` — он асинхронный и есть только
 * в защищённом контексте (https или localhost). Соль и строка `salt:pin`
 * те же, что в Dart, и вывод тот же: нижний регистр шестнадцатеричных
 * цифр, как у `sha256.convert(...).toString()`. Более слабой замены нет
 * намеренно: она пускала бы в киоск по чужому коду.
 */
async function hashPin(pin: number): Promise<string> {
  const subtle: SubtleCrypto | undefined = globalThis.crypto?.subtle;
  if (subtle === undefined) {
    throw new Error(
      'crypto.subtle недоступен: хеш пин-кода считается только в защищённом ' +
        'контексте (https или localhost)',
    );
  }
  const bytes = new TextEncoder().encode(`${PIN_SALT}:${pin}`);
  const digest = await subtle.digest('SHA-256', bytes);
  return toHexLowerCase(digest);
}

/** Байты в строку нижнего регистра — формат вывода `sha256` из Dart. */
function toHexLowerCase(digest: ArrayBuffer): string {
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}
